import { registerErrorMiddleware, useErrorStore } from '@ugi/core';
import { isGranted } from '@pcg/auth';

import { useAuthStore } from '@/stores/auth';

import AccessDeniedPage from '../pages/AccessDeniedPage.vue';
import NotFoundPage from '../pages/NotFoundPage.vue';
import ServerErrorPage from '../pages/ServerErrorPage.vue';
import { createRouter, createWebHistory } from 'vue-router';
import { authMiddleware } from '../middlewares/auth';
import { permissionMiddleware } from '../middlewares/permission';
import { setCurrentScreen } from '../apollo/current-screen';

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior: () => ({
    top: 0,
  }),
  routes: [
    {
      path: '/',
      name: 'Home',
      component: { template: '<div />' },
      /**
       * Куди веде «/» — це перший екран після кожного входу, тож він мусить
       * бути екраном, який ця людина справді може відкрити.
       *
       * Раніше тут стояв безумовний відкат на `Students`. Поки в маршруту
       * студентів не було гарда, диспетчер (він не має ані `bot:chat:*`, ані
       * `students:*`) просто бачив порожню таблицю — погано, але не помилка.
       * Щойно маршрут отримав `authorize`, той самий відкат став екраном
       * «Немає доступу» одразу після входу.
       *
       * Тому вибір іде по черзі й закінчується «Довідкою», яка відкрита всім:
       * жодна роль не має приземлятися на 403.
       */
      beforeEnter: () => {
        const auth = useAuthStore();
        const user = auth.user;

        if (!user) {
          return { name: 'Students' };
        }

        if (isGranted(user, 'ugi:bot:chat:dashboard')) {
          return { name: 'Dashboard' };
        }

        // `lessons:update`, не `:list`. Розклад читають усі — і студент, і
        // викладач мають `lessons:get|list`, щоб бачити свої пари, — тож на
        // `:list` цей рядок відправляв би їх у редактор розкладу диспетчера.
        // Редагувати розклад може лише диспетчер, і саме він тут і мається на
        // увазі. Стоїть раніше за `students:list`: диспетчер має й його (щоб
        // дописувати студентів у когорти), а його робочий екран — розклад.
        if (isGranted(user, 'ugi:core:lessons:update')) {
          return { name: 'Schedule' };
        }

        if (isGranted(user, 'ugi:core:students:list')) {
          return { name: 'Students' };
        }

        return { name: 'Help' };
      },
      meta: {
        layout: 'main',
      },
    },
    {
      path: '/access-denied',
      name: 'AccessDenied',
      component: AccessDeniedPage,
      meta: {
        layout: 'empty',
        public: true,
      },
    },
    {
      path: '/server-error',
      name: 'ServerError',
      component: ServerErrorPage,
      meta: {
        layout: 'empty',
        public: true,
      },
    },
    // Catch-all 404 route - must be registered last
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: NotFoundPage,
      meta: {
        layout: 'main',
      },
    },
  ],
});

// Clear stale errors first, before any guards that may set new ones
registerErrorMiddleware(router);
router.beforeEach(authMiddleware);
router.beforeEach(permissionMiddleware);

// Журнал змін: запити з цього екрана підписуються його назвою (`x-ugi-screen`).
router.afterEach((to) => {
  setCurrentScreen(typeof to.name === 'string' ? to.name : undefined);
});

// Handle chunk loading errors after rebuild
router.onError((error) => {
  if (
    error.message.includes('Failed to fetch dynamically imported module')
    || error.message.includes('Importing a module script failed')
  ) {
    const errorStore = useErrorStore();
    errorStore.setError({
      statusCode: 418,
      title: 'Application Updated',
      description:
        'A new version of the application has been released. Please reload the page to get the latest updates.',
      critical: true,
    });
  }
});

export default router;
